/**
 * @function catRaisonneViewer
 *
 * @description Catalog Raisonnée data-driven carousel with accordion data display
 *
 * TODO: Virtualize?
 * TODO: Loading indicator?
 **/

import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import ReactDOM from 'react-dom/client';
import DOMPurify from 'dompurify';

import { CatalogCarousel } from './carousel.jsx';
import { QuickViewer } from '../quickView.js';
import { SearchConfigProvider, SearchConfigContext } from '../../context/searchConfigContext.js';
import { FALLBACK_IMAGE_SIZES, getFallbackImageUrl, useElasticSearch, sizeResettingErrorHandler } from '../../util';

const blockSize = 20
const defaultQuery = { query: { bool: { must: [{terms: { type: ['cr-object'] }}, {range: {cr_number_order: {gte: 0, lte: 9000}}}] }},
                       size: blockSize, 
                       sort: [{cr_number_order: {order: 'asc'}},{ cr_number: {order: 'asc'} }] }

const makeIdsQuery = (ids) => {
  return {
    query: { ids: { values: ids }},
    size: ids.length,
    sort: [{cr_number_order: {order: 'asc'}},{ cr_number: {order: 'asc'} }] 
  }
}

const makeInitQuery = () => {
  let request = window.structuredClone(defaultQuery)

  request.query.bool.must.pop()
  request.size = 3000
  request._source = false

  return request
}

const allIdsQuery = makeInitQuery()

const below = (sortId) => {
  return {
    query: {
      range: {
        cr_number_order: {
          lt: sortId
        }
      }
    },
    size: Math.round(blockSize / 2),
    sort: { cr_number_order: "desc" }
  }
}

const above = (sortId, equal=true) => {
  const cr_number_order = equal ? { gte: sortId } : { gt: sortId }
  return {
    query: {
      range: {
        cr_number_order
      }
    },
    size: Math.round(blockSize / 2),
    sort: { cr_number_order: "asc" }
  }
}

const windowQueries = (sortId) => `{}\n${JSON.stringify(below(sortId))}\n{}\n${JSON.stringify(above(sortId))}\n`

export const CatRaisonneViewer = (props) => {

    // Elastic setup -- consider omitting in favor of static JSON at startup
    const [endpoint, setEndpoint] = useState(props.endpoint)
    const [query, setQuery] = useState(null)

    const [catalog, setCatalog] = useState()

    // NB: crOrder is a sortable number but crNumber is a string
    const [crOrder, setCrOrder] = useState(props.catalogEntry.orders.cr_order ?? 0)
    const [crNumber, setCrNumber] = useState(props.catalogEntry.cr_number)

    const [itemsCount, setItemsCount] = useState()
    const [selected, setSelected] = useState(props.catalogEntry._id)
    const [selectedIndex, setSelectedIndex] = useState(undefined)

    const [prependedCount, setPrependedCount] = useState(0)

    const [loaded, setLoaded] = useState(false)

    const loadMoreRequest = useElasticSearch(endpoint, query);

    const initialWindow = useMemo(() => windowQueries(crOrder), [crOrder])
    const initialRequest = useElasticSearch(endpoint.replace('/_search', '/_msearch'), initialWindow)

    /**
     * Handler for initial multisearch request, which interleaves two queries
     * 
     * - Adds up each result wing's count to set total count
     * - Merges each wing's hits, reversing the lower wing to ensure ordering
     *
     **/ 
    useEffect(() => {
      if (!initialRequest) return

      let catalogStart, catalogEnd
      let count = 0

      // Construct the initial window of results
      initialRequest.responses.forEach((response, index) => {
        count += response.hits.total

        if (index > 0) {
          catalogEnd = [...response.hits.hits]            
        } else {
          // NB: catalog is ascending-sorted so reverse the descending-start window 
          catalogStart = [...response.hits.hits.reverse()]
        }
      })

      setCatalog([...catalogStart, ...catalogEnd])

      const selectedIndex = [...catalogStart, ...catalogEnd].findIndex((cat) => cat._source.cr_number_order === crOrder)
      setSelectedIndex(selectedIndex)

      setItemsCount(count)
      setLoaded(true)
    }, [initialRequest])

    useEffect(() => {
      if (!loadMoreRequest) return

      const lastItem = loadMoreRequest.hits.hits.at(-1)
      if (!lastItem) return

      const size = loadMoreRequest.hits.hits.length
      const lastItemSort = lastItem?.sort?.at(0)

      // Use last item of query window to insert to catalog head or tail
      if (lastItemSort < crOrder) {
        setCatalog([...loadMoreRequest.hits.hits.reverse(), ...catalog])
        setPrependedCount(prependedCount + loadMoreRequest.hits.hits.length)
      } else {
        setCatalog([...catalog, ...loadMoreRequest.hits.hits])
      }
    }, [loadMoreRequest])

    /**
     * @function loadMoreData
     * 
     * @param {Boolean} begin 
     * 
     * Loads more data from the front or end of the `array`
     * 
     **/ 
    const loadMoreData = (startOrEnd) => {
      // Get both ends of the current result window
      const firstLesser = catalog.find((cat) => cat._source.cr_number_order && cat._source.cr_number_order < crOrder)
      const lastGreater = catalog.findLast((cat) => cat._source.cr_number_order && cat._source.cr_number_order > crOrder)

      switch (true) {
        case (startOrEnd === 'start' && firstLesser !== undefined):
          setQuery(below(firstLesser._source.cr_number_order))
          break
        
        case (startOrEnd === 'end' && lastGreater !== undefined):
          setQuery(above(lastGreater._source.cr_number_order, false))
          break

        default:
          // Result window is at beginning or end, do nothing
          return
      }
    }

    return (
      <div className="mx-0">
        <div className="section-relationships-r">
          <div className="mx-0 px-0">
            <div className="rels-heading bg-bone-180">
              <div className="page-headline" role="heading" aria-level="3">
                Catalogue Raisonné
              </div>
            </div>
            <div className="carousel-filter is-hidden">
              <div className="dropdown-left-label">Filter by:</div>
              <div id="" className="select filter-material">
                <select
                  className="filter-dd"
                  id="filter-material"
                  aria-labelledby="filter-material">
                  <option>Materials</option>
                  <option>Option 2</option>
                  <option>Option 3</option>
                </select>
              </div>
            </div>
            <div className={`carousel-container ${!loaded ? 'loading-image' : '' }`}>
              <CatalogCarousel blockSize={blockSize}
                               catalog={catalog} 
                               count={itemsCount} 
                               loadMore={loadMoreData}
                               prepended={prependedCount}
                               selected={selected}
                               selectedIndex={selectedIndex}
                               setSelected={setSelected}
                               setSelectedIndex={setSelectedIndex} />
            </div>
          </div>
        </div>
      </div>
    );

}

// Load into the id'd root if available
window.addEventListener('DOMContentLoaded', (event) => {
    const elementRoot = document.getElementById('catRaisonneViewerRoot')

    // TODO: Adjust/align the mapping to actual
    if (elementRoot) {
        const { indexName, endpoint } = elementRoot.dataset;

        const endpointPath = indexName ? `${indexName}/_search`: 'entity/_search'
        const endpointUrl = new URL(endpointPath, endpoint);

        let catalogEntry
        if (elementRoot.firstElementChild && elementRoot.firstElementChild.tagName === 'SCRIPT') {
            const content = DOMPurify.sanitize(elementRoot.firstElementChild.innerHTML)

            try {
                catalogEntry = JSON.parse(content)
            } catch (err) {
                console.warn("Could not parse embedded CR data.")
            }
        }

        const mapping = {
            displayType: (dataSource) => { return 'Artwork' },
            dateLabel: (dataSource) => {

              if ( typeof dataSource._source.timespan?.label === "string" ) {
                return dataSource._source.timespan?.label
              } if ( Array.isArray(dataSource._source.timespan?.label) ) {
                return dataSource._source.timespan?.label.join(', ')
              } else {
                return dataSource._source.timespan?.label ?? ''
              }
            },
            makers: (dataSource) => { return dataSource._source.artist_label?.join('<br>') ?? '' },
            materials: (dataSource) => { return dataSource._source.classified_as?.join(', ') ?? '' },
            dimension: (dataSource) => { return dataSource._source.extent?.join(', ') ?? '' },
            isArtObject: (dataSource) => { return (dataSource._source) && (dataSource._source.type) && (typeof dataSource._source.type == 'string') && (dataSource._source.type == 'art') ? true : false },
            isReference: (dataSource) => { return false }, // For now, all relationships are in-GOKM
            label: (dataSource) => { return Array.isArray(dataSource._source.label) ? dataSource._source.label.join('') : dataSource._source.label },
            link: (dataSource) => { return dataSource._id.startsWith('http://') || dataSource._id.startsWith('https://') ? dataSource._id : `/${dataSource._id}` },
            imageSrcSet: (dataSource,baseWidth=242,densities=[1,2,3]) => {
              let primaryUrl = dataSource._source.representation?.primary;

              if (primaryUrl === undefined || primaryUrl === null) {
                return primaryUrl
              }

              let height = dataSource._source.representation?.height ?? 9999;
              let width = dataSource._source.representation?.width ?? 9999;

              let url = new URL(primaryUrl);
              let iiifParams = url.pathname.split('/').slice(-4).join('/');

              return densities.map( density => {

                  let densityParams = [ 'full', `,${ Math.min( Number(height), baseWidth*density) }`, '0', 'default.jpg' ].join('/')
                  let imageIdPath = primaryUrl.replace(iiifParams,densityParams)

                  return `${imageIdPath} ${Number(density)}x`;

                }).join(', ')
            },
            imageUrl: (dataSource) => { return dataSource._source.representation?.primary?.replace('square/!240,240', 'full/,242') ?? getFallbackImageUrl(dataSource,FALLBACK_IMAGE_SIZES.LARGE) }, // returns URL
            description: (dataSource) => { return '' },
            credit: (dataSource) => {
              return ( dataSource._source.caption?.credit ?? []).join(', ');
            },
            copyright: (dataSource) => {
              return ( dataSource._source.caption?.rights ?? []).join(', ') ;
            },
            currentOwner: (dataSource) => {
              return ( dataSource._source.caption?.owner ?? []).join(', ') ;
            },
            accession: (dataSource) => {
              return ( dataSource._source.accession ?? []).join(', ') ;
            }
        }

        ReactDOM.createRoot(elementRoot).render(<CatRaisonneViewer mapping={{...mapping}} endpoint={endpointUrl.href} catalogEntry={catalogEntry} />);
    }
});
